import type { ReactNode } from "react";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { useIsAuthenticated } from "@/stores/authStore";
import { ChatSocketProvider } from "@/features/chat/providers";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Main application layout with sidebar navigation
 * Used for authenticated pages (dashboards, etc.)
 */
export function AppLayout({ children }: AppLayoutProps) {
  return (
    <ChatSocketProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
            <SidebarTrigger className="mb-4 lg:hidden" />
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </ChatSocketProvider>
  );
}

/**
 * Public layout that shows sidebar for authenticated users
 * Used for public pages like openings that benefit from sidebar when logged in
 */
export function PublicAppLayout({ children }: AppLayoutProps) {
  const isAuthenticated = useIsAuthenticated();

  if (isAuthenticated) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
            <SidebarTrigger className="mb-4 lg:hidden" />
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // For unauthenticated users, show a minimal layout
  return (
    <div className="bg-background min-h-screen">
      <main className="flex flex-1 flex-col p-4 sm:p-6 lg:p-8">{children}</main>
    </div>
  );
}

/**
 * Minimal layout without sidebar
 * Used for public pages (login, landing, etc.)
 */
export function MinimalLayout({ children }: AppLayoutProps) {
  return (
    <div className="bg-background min-h-screen">
      <main>{children}</main>
    </div>
  );
}

/**
 * Centered layout for auth pages
 * Used for login, register, etc.
 */
export function AuthLayout({ children }: AppLayoutProps) {
  return (
    <div className="bg-muted/40 flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
